import { create } from "zustand";
import type { JourneyPhaseKind } from "../components/ai-coach/types";

export interface ChatSidebarFocus {
  programId?: string;
  targetId?: string;
}

interface ChatSidebarState {
  isOpen: boolean;
  mode: JourneyPhaseKind | null;
  focus: ChatSidebarFocus | null;
  // Context-mismatch banner: true while `mode` was auto-set to match the
  // current page rather than chosen explicitly (e.g. via a card deep link).
  pageMatchActive: boolean;
  // Pathname the user last dismissed the banner for (via "back to default"
  // or by sending a message), so re-applying the auto-match doesn't
  // immediately fight the dismissal until the user navigates again.
  dismissedForPath: string | null;
  openChat: (opts?: {
    mode?: JourneyPhaseKind;
    focus?: ChatSidebarFocus;
  }) => void;
  closeChat: () => void;
  toggle: () => void;
  applyPageMatch: (mode: JourneyPhaseKind) => void;
  dismissPageMatch: (pathname: string) => void;
  revertToDefault: (pathname: string) => void;
  clearPageMatch: () => void;
}

// Global entry point for the sidebar-docked coach chat (portal-rendered,
// mounted once in Layout). Replaces per-page AiCoachChat embeds and the
// /ai-advisor route for the main app. ProgramAIPanel keeps its own separate
// AiCoachChat instance/store (lite-user program editor path, out of scope).
export const useChatSidebarStore = create<ChatSidebarState>((set) => ({
  isOpen: false,
  mode: null,
  focus: null,
  pageMatchActive: false,
  dismissedForPath: null,
  openChat: (opts) =>
    set({
      isOpen: true,
      mode: opts?.mode ?? null,
      focus: opts?.focus ?? null,
      pageMatchActive: false,
    }),
  closeChat: () => set({ isOpen: false }),
  toggle: () => set((s) => ({ isOpen: !s.isOpen })),
  applyPageMatch: (mode) =>
    set({ mode, focus: null, pageMatchActive: true, dismissedForPath: null }),
  dismissPageMatch: (pathname) =>
    set({ pageMatchActive: false, dismissedForPath: pathname }),
  revertToDefault: (pathname) =>
    set({
      mode: null,
      focus: null,
      pageMatchActive: false,
      dismissedForPath: pathname,
    }),
  // Like `revertToDefault`, but doesn't mark the current path as dismissed.
  // Used when the override is cleared as a side effect of navigating away
  // (e.g. to an unmapped page) rather than by the user explicitly backing
  // out via the banner — the page being landed on should still be able to
  // show the mismatch banner if it applies.
  clearPageMatch: () =>
    set({ mode: null, focus: null, pageMatchActive: false }),
}));
